// ExplorePH3/client/src/App.jsx - Main App component for ExplorePH
import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { 
  SignedIn, 
  SignedOut, 
  SignInButton, 
  UserButton, 
  useUser 
} from '@clerk/clerk-react';
import { 
  MapPin, 
  List, 
  User as UserIcon, 
  Compass, 
  Search, 
  Filter,
  Menu,
  X,
  Bell,
  Settings,
  LogOut
} from 'lucide-react';
import axios from 'axios';
import './App.css';

// Import components
import BottomNav from './components/BottomNav';
import VenueCard from './components/VenueCard';
import MapComponent from './components/MapComponent';

// API base URL - use relative path in production, full URL in development
const isProduction = import.meta.env.VITE_ENVIRONMENT === 'production';
const API_BASE_URL = isProduction ? '' : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787');

// Main App Component
function ExplorePHApp() {
  const { user, isLoaded } = useUser();
  const [viewMode, setViewMode] = useState('map'); // 'map' or 'list'
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [error, setError] = useState(null);

  // Fetch user location
  const fetchUserLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return Promise.resolve([14.5995, 120.9842]); // Default to Manila
    }

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = [position.coords.latitude, position.coords.longitude];
          setUserLocation(location);
          resolve(location);
        },
        (error) => {
          console.error('Geolocation error:', error);
          setError('Unable to get your location. Using default location.');
          const defaultLocation = [14.5995, 120.9842]; // Manila
          setUserLocation(defaultLocation);
          resolve(defaultLocation);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });
  }, []);

  // Fetch venues based on location
  const fetchVenues = useCallback(async (center) => {
    if (!center) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const [lat, lng] = center;
      const response = await axios.get(`${API_BASE_URL}/api/venues`, {
        params: {
          lat,
          lng,
          radius_km: 5 // 5km radius
        },
        // Add timeout for production
        timeout: 10000
      });

      if (response.data.success) {
        setVenues(response.data.data || []);
      } else {
        setError('Failed to load venues');
      }
    } catch (err) {
      console.error('Error fetching venues:', err);
      setError('Unable to load venues. Please try again.');
      
      // Fallback to mock data for development
      if (API_BASE_URL.includes('localhost')) {
        setVenues(getMockVenues());
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle check-in
  const handleCheckIn = async (venueId) => {
    if (!user) {
      // User will be prompted to sign in by VenueCard
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/vibe`,
        {
          venue_id: venueId,
          vibe_type: 'fire' // Default to fire for now
        },
        {
          headers: {
            'Authorization': `Bearer ${await user.getToken()}`
          },
          // Add timeout for production
          timeout: 5000
        }
      );

      if (response.data.success) {
        // Update the venue's vibe in local state
        setVenues(prevVenues =>
          prevVenues.map(venue =>
            venue.id === venueId
              ? { 
                  ...venue, 
                  current_vibe: 'fire',
                  recent_checkins: (venue.recent_checkins || 0) + 1 
                }
              : venue
          )
        );
        
        return response.data;
      }
    } catch (err) {
      console.error('Error checking in:', err);
      throw err;
    }
  };

  // Handle map movement
  const handleMapMove = async (mapData) => {
    // In a real app, you might want to fetch venues for the new viewport
    // For now, we'll just log it
    console.log('Map moved:', mapData);
  };

  // Initial data loading
  useEffect(() => {
    const loadData = async () => {
      try {
        const location = await fetchUserLocation();
        await fetchVenues(location);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load initial data');
        setLoading(false);
      }
    };

    loadData();
  }, [fetchUserLocation, fetchVenues]);

  // Mock data for development
  const getMockVenues = () => [
    {
      id: 1,
      name: 'Las Cabanas Beach Bar',
      description: 'Beachfront bar with stunning sunset views',
      latitude: 11.2014,
      longitude: 119.4063,
      address: 'Las Cabanas, El Nido, Palawan',
      image_url: 'https://images.unsplash.com/photo-1514933651103-005eec06c04b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      current_vibe: 'fire',
      average_rating: 4.5,
      recent_checkins: 24,
      open_hours: 'Open until 2 AM',
      distance_km: 0.5
    },
    {
      id: 2,
      name: 'Artcafe El Nido',
      description: 'Cozy cafe with live music and art exhibitions',
      latitude: 11.2020,
      longitude: 119.4070,
      address: 'El Nido Town Proper, Palawan',
      image_url: 'https://images.unsplash.com/photo-1559925393-8be0ec4767c8?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      current_vibe: 'chill',
      average_rating: 4.2,
      recent_checkins: 18,
      open_hours: '8 AM - 10 PM',
      distance_km: 1.2
    },
    {
      id: 3,
      name: 'Nacpan Beach Lounge',
      description: 'Relaxed beach lounge with hammocks and cocktails',
      latitude: 11.2030,
      longitude: 119.4080,
      address: 'Nacpan Beach, El Nido, Palawan',
      image_url: 'https://images.unsplash.com/photo-1518548419970-58e3b4079ab2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      current_vibe: 'dead',
      average_rating: 4.0,
      recent_checkins: 8,
      open_hours: '10 AM - 8 PM',
      distance_km: 2.5
    }
  ];

  // Header component
  const Header = () => (
    <header className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
      <div className="px-4 h-16 flex items-center justify-between">
        {/* Logo and title */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="p-2 rounded-lg hover:bg-gray-100 active:scale-95 transition-transform md:hidden"
            aria-label="Menu"
          >
            {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
              <Compass className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900 hidden sm:inline">ExplorePH</span>
          </div>
        </div>

        {/* Search bar */}
        <div className="flex-1 max-w-md mx-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search venues..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-100 rounded-xl border-0 focus:ring-2 focus:ring-teal-500 focus:bg-white transition-all"
            />
          </div>
        </div>

        {/* User actions */}
        <div className="flex items-center gap-2">
          <button className="p-2 rounded-lg hover:bg-gray-100 active:scale-95 transition-transform relative">
            <Bell className="w-5 h-5" />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
          
          <div className="hidden sm:block">
            {user ? (
              <UserButton 
                afterSignOutUrl="/"
                appearance={{
                  elements: {
                    userButtonAvatarBox: "w-10 h-10"
                  }
                }}
              />
            ) : (
              <SignInButton mode="modal">
                <button className="px-4 py-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl font-medium hover:shadow-lg active:scale-95 transition-all">
                  Sign In
                </button>
              </SignInButton>
            )}
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden border-t bg-white">
          <div className="px-4 py-3 space-y-2">
            <button className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100">
              <Settings className="w-5 h-5" />
              <span>Settings</span>
            </button>
            <button className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100">
              <Filter className="w-5 h-5" />
              <span>Filters</span>
            </button>
            <SignedIn>
              <button className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 text-red-600">
                <LogOut className="w-5 h-5" />
                <span>Sign Out</span>
              </button>
            </SignedIn>
          </div>
        </div>
      )}
    </header>
  );

  // View toggle component
  const ViewToggle = () => (
    <div className="fixed top-20 right-4 z-40 bg-white rounded-full shadow-hard p-1">
      <div className="flex">
        <button
          onClick={() => setViewMode('map')}
          className={`px-4 py-2 rounded-full transition-all ${
            viewMode === 'map'
              ? 'bg-gradient-to-r from-teal-500 to-teal-600 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <MapPin className="w-5 h-5" />
        </button>
        <button
          onClick={() => setViewMode('list')}
          className={`px-4 py-2 rounded-full transition-all ${
            viewMode === 'list'
              ? 'bg-gradient-to-r from-teal-500 to-teal-600 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <List className="w-5 h-5" />
        </button>
      </div>
    </div>
  );

  // Loading skeleton
  const LoadingSkeleton = () => (
    <div className="p-4 space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="bg-white rounded-2xl shadow-medium overflow-hidden">
          <div className="h-48 skeleton"></div>
          <div className="p-4 space-y-3">
            <div className="h-6 w-3/4 skeleton rounded"></div>
            <div className="h-4 w-1/2 skeleton rounded"></div>
            <div className="h-12 skeleton rounded-xl"></div>
          </div>
        </div>
      ))}
    </div>
  );

  // Error display
  const ErrorDisplay = () => (
    <div className="p-8 text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
        <X className="w-8 h-8 text-red-600" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Something went wrong</h3>
      <p className="text-gray-600 mb-4">{error}</p>
      <button
        onClick={() => {
          setError(null);
          fetchUserLocation().then(fetchVenues);
        }}
        className="px-6 py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl font-medium hover:shadow-lg active:scale-95 transition-all"
      >
        Try Again
      </button>
    </div>
  );

  // Main content
  const MainContent = () => {
    if (error) return <ErrorDisplay />;
    
    return (
      <>
        <ViewToggle />
        
        {viewMode === 'map' ? (
          // Map View
          <div className="absolute inset-0 pt-16 pb-20">
            <MapComponent
              venues={venues}
              userLocation={userLocation}
              onVenueClick={setSelectedVenue}
              onCheckIn={handleCheckIn}
              onMapMove={handleMapMove}
              loading={loading}
              className="h-full"
            />
          </div>
        ) : (
          // List View
          <div className="pt-16 pb-24 px-4">
            <div className="max-w-2xl mx-auto">
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Nearby Venues
                </h1>
                <p className="text-gray-600">
                  {venues.length} venue{venues.length !== 1 ? 's' : ''} near you
                </p>
              </div>
              
              {loading ? (
                <LoadingSkeleton />
              ) : venues.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                    <MapPin className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No venues found</h3>
                  <p className="text-gray-600">Try adjusting your location or search</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {venues.map((venue) => (
                    <VenueCard
                      key={venue.id}
                      venue={venue}
                      onCheckIn={handleCheckIn}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <main className="flex-1 relative">
        <MainContent />
      </main>
      <BottomNav />
    </div>
  );
}

// App wrapper with Clerk provider
function App() {
  const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

  if (!clerkPubKey) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <X className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Configuration Error</h1>
          <p className="text-gray-600 mb-4">
            Clerk publishable key is missing. Please check your environment variables.
          </p>
          <code className="block p-3 bg-gray-100 rounded-lg text-sm font-mono">
            VITE_CLERK_PUBLISHABLE_KEY is not set
          </code>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<ExplorePHApp />} />
      <Route path="/map" element={<ExplorePHApp />} />
      <Route path="/list" element={<ExplorePHApp />} />
      <Route path="/profile" element={<ExplorePHApp />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default App;
